import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AxiosGetMethod, AxiosPostMethod } from "../helpers/customAxios";
import PaymentComponent from "../components/payment-gateway";
import { ErrorToast, SuccessToast } from "../components/toastify";
import { setActivePage, setShowPreloader } from "../store/activePage";
import {
  REGISTRATION_PAYMENT_STORAGE_KEY,
  isRegistrationPaymentPending,
} from "../helpers/operatorSession";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));

function RegistrationPaymentScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const autoOpened = useRef(false);

  const storeStatus = useCallback((status) => {
    setPaymentStatus(status);
    if (status?.org_id) {
      localStorage.setItem("org-id", String(status.org_id));
    }
    localStorage.setItem(
      REGISTRATION_PAYMENT_STORAGE_KEY,
      JSON.stringify(status || {}),
    );
  }, []);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    const res = await AxiosGetMethod("org/registration-payment/status");
    if (res?.status === 200) {
      storeStatus(res.data?.data || null);
    } else {
      ErrorToast(res?.data?.message || "Unable to fetch registration payment status.");
    }
    setLoading(false);
  }, [storeStatus]);

  const startPayment = useCallback(async () => {
    const orgId =
      paymentStatus?.org_id || Number(localStorage.getItem("org-id") || 0);
    if (!orgId) {
      ErrorToast("Organisation ID is missing. Please login again and try payment.");
      return;
    }

    dispatch(setShowPreloader(true));
    const orderRes = await AxiosPostMethod("org/registration-payment/order-create", {
      org_id: orgId,
    });
    dispatch(setShowPreloader(false));

    if (orderRes?.status !== 200) {
      ErrorToast(orderRes?.data?.message || "Unable to create registration payment order.");
      return;
    }

    if (orderRes?.data?.data?.already_paid) {
      storeStatus(orderRes?.data?.data?.payment_status || paymentStatus);
      SuccessToast("Registration payment already completed.");
      navigate("/dashboard/home", { replace: true });
      return;
    }

    PaymentComponent(
      orderRes.data.data,
      async (razval) => {
        dispatch(setShowPreloader(true));
        const updateRes = await AxiosPostMethod("org/registration-payment/update", {
          ...razval,
          org_id: orgId,
        });
        dispatch(setShowPreloader(false));

        if (updateRes?.status === 200) {
          storeStatus(updateRes.data?.data || null);
          SuccessToast("Registration payment completed successfully.");
          navigate("/dashboard/home", { replace: true });
        } else {
          ErrorToast(updateRes?.data?.message || "Unable to update registration payment.");
        }
      },
      () => ErrorToast("Registration payment failed or was cancelled."),
    );
  }, [dispatch, navigate, paymentStatus, storeStatus]);

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Registration Payment",
        activeMenu: "registrationPayment",
      }),
    );
    fetchStatus();
  }, [dispatch, fetchStatus]);

  useEffect(() => {
    if (!loading && paymentStatus?.paid) {
      navigate("/dashboard/home", { replace: true });
      return;
    }

    if (
      !loading &&
      isRegistrationPaymentPending(paymentStatus) &&
      !autoOpened.current
    ) {
      autoOpened.current = true;
      startPayment();
    }
  }, [loading, navigate, paymentStatus, startPayment]);

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#edf5f8_0%,#f8fbfd_45%,#edf4f8_100%)] px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-2xl rounded-3xl border border-white/80 bg-white/95 p-8 shadow-xl shadow-slate-200/70">
        <p className="text-sm font-bold uppercase tracking-[0.22em] text-teal-700">
          Registration Payment
        </p>
        <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
          Complete your enterprise registration
        </h1>
        <p className="mt-3 text-sm leading-6 text-slate-500">
          Your organisation account is created, but dashboard access stays locked until
          the one-time registration fee is paid.
        </p>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
            Amount to pay
          </p>
          <p className="mt-2 text-4xl font-black tracking-tight text-slate-950">
            {formatCurrency((paymentStatus?.amount_in_paise || 0) / 100)}
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Status: {paymentStatus?.paid ? "Paid" : "Pending"}
          </p>
          {paymentStatus?.paid_at ? (
            <p className="mt-1 text-xs text-slate-500">
              Paid on: {paymentStatus.paid_at}
            </p>
          ) : null}
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={startPayment}
            className="inline-flex items-center justify-center rounded-xl bg-teal-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-teal-700"
          >
            Pay Now
          </button>
          <button
            type="button"
            onClick={fetchStatus}
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:border-slate-300"
          >
            Refresh Status
          </button>
        </div>
      </div>
    </div>
  );
}

export default RegistrationPaymentScreen;
